/**
 * Photo upload helpers.
 *
 * In the Supabase era this file talked to `supabase.storage` directly. After the
 * Azure migration, all uploads go through `POST /api/uploads/:container`. The
 * exported names match the old surface so callers in `components/` migrate with
 * a one-line import change.
 */
import { apiClient, type UploadResult } from './apiClient';
import { logEvent } from './telemetry';

export const PHOTO_BUCKETS = {
  INSPECTION: 'inspection-photos',
  CONTROLLER: 'controller-photos',
  ZONE: 'zone-photos',
  DOUBLE_CHECK: 'double-check-photos',
  WATER_METER: 'water-meter-photos',
  FEEDBACK: 'feedback-screenshots',
  COMPANY_LOGO: 'company-logos',
  USER: 'user-photos',
} as const;

export type PhotoBucket = typeof PHOTO_BUCKETS[keyof typeof PHOTO_BUCKETS];

// Field photos come straight off an iPad camera at 6-8 MB, which routinely fail
// to finish uploading over weak field cellular (the connection drops mid-transfer
// and the request aborts). Downscale them in the browser before upload so they
// are a few hundred KB and survive a poor signal. Scoped to the device-photo
// buckets only — logos / screenshots / avatars are small and may be transparent
// PNGs we don't want flattened to JPEG.
const DOWNSCALE_BUCKETS = new Set<string>([
  PHOTO_BUCKETS.INSPECTION,
  PHOTO_BUCKETS.CONTROLLER,
  PHOTO_BUCKETS.ZONE,
  PHOTO_BUCKETS.DOUBLE_CHECK,
  PHOTO_BUCKETS.WATER_METER,
]);

const UPLOAD_MAX_DIM = 1600; // longest edge, matches the server-side PDF downscale
const UPLOAD_JPEG_QUALITY = 0.8;

/**
 * Best-effort client-side downscale. Decodes the image (honoring EXIF
 * orientation), caps the longest edge at UPLOAD_MAX_DIM, and re-encodes as JPEG.
 * Returns the ORIGINAL file unchanged if anything fails (unsupported format like
 * HEIC the browser can't decode, no canvas, or the result isn't smaller) so it
 * can never block or corrupt an upload. Keeps the original filename so caller
 * paths are untouched.
 */
export async function downscaleImageForUpload(file: File): Promise<File> {
  if (typeof document === 'undefined' || !file.type.startsWith('image/')) return file;
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > UPLOAD_MAX_DIM ? UPLOAD_MAX_DIM / longest : 1;
    // Already small and already a JPEG → nothing to gain, skip the re-encode.
    if (scale === 1 && file.type === 'image/jpeg') {
      bitmap.close();
      return file;
    }
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', UPLOAD_JPEG_QUALITY),
    );
    if (!blob || blob.size >= file.size) return file;
    void logEvent('photo_downscaled_for_upload', 'Downscaled photo before upload', {
      metadata: {
        original_bytes: file.size,
        new_bytes: blob.size,
        original_dim: `${bitmap.width}x${bitmap.height}`,
        new_dim: `${w}x${h}`,
        type: file.type,
      },
    });
    return new File([blob], file.name, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file; // best-effort: never block the upload
  }
}

/** Downscale (for the device-photo buckets) then hand off to the uploader. */
async function prepareForUpload(file: File, bucket: PhotoBucket): Promise<File> {
  return DOWNSCALE_BUCKETS.has(bucket) ? downscaleImageForUpload(file) : Promise.resolve(file);
}

export async function uploadPhotoFile(
  file: File,
  bucket: PhotoBucket,
  path: string,
): Promise<string> {
  const toSend = await prepareForUpload(file, bucket);
  const result = await apiClient.upload(bucket, toSend, { path, filename: toSend.name });
  return result.url;
}

export async function uploadPhotoFileVerbose(
  file: File,
  bucket: PhotoBucket,
  path: string,
): Promise<UploadResult> {
  const toSend = await prepareForUpload(file, bucket);
  return apiClient.upload(bucket, toSend, { path, filename: toSend.name });
}

export function buildSinglePhotoPath(folder: string, id: string, fileExt: string): string {
  return `${folder}/${id}/photo.${fileExt}`;
}

export function buildTimestampedPhotoPath(prefix: string, id: string): string {
  return `${prefix}_${id}_${Date.now()}.jpg`;
}

export function dataUrlToBlob(dataUrl: string): Blob {
  const base64Data = dataUrl.split(',')[1];
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([byteNumbers], { type: 'image/jpeg' });
}

export async function uploadDataUrlToStorage(
  dataUrl: string,
  bucket: PhotoBucket,
  path: string,
): Promise<string> {
  const blob = dataUrlToBlob(dataUrl);
  const file = new File([blob], path.split('/').pop() || 'photo.jpg', { type: 'image/jpeg' });
  return uploadPhotoFile(file, bucket, path);
}

/**
 * Delete a single blob. The previous Supabase helper took a folder and listed
 * everything under it; with Blob there is no list-then-delete in one call —
 * call sites that need that behavior should track their own paths.
 */
export async function deletePhoto(bucket: PhotoBucket, path: string): Promise<void> {
  await apiClient.del<void>(`/api/uploads/${encodeURIComponent(bucket)}/${path
    .split('/')
    .map(encodeURIComponent)
    .join('/')}`);
}

const KNOWN_BUCKETS = new Set<string>(Object.values(PHOTO_BUCKETS));

/**
 * Delete a blob from one of our managed containers given its full public URL.
 * Use this when removing a `photo_url` column or trimming a `photo_urls` array
 * — without it, the row drops the reference but the blob lives forever.
 *
 * No-ops on falsy URLs, on URLs that point outside our containers, and on 404
 * responses (the blob may have already been deleted by a prior attempt). All
 * other errors are logged but swallowed so the calling save flow never fails
 * because of orphan cleanup.
 */
export async function deletePhotoByUrl(url: string | null | undefined): Promise<void> {
  if (!url) return;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return;
  }
  const segments = parsed.pathname.split('/').filter(Boolean);
  if (segments.length < 2) return;
  const [container, ...pathSegments] = segments;
  if (!KNOWN_BUCKETS.has(container)) return;
  try {
    await apiClient.del<void>(
      `/api/uploads/${encodeURIComponent(container)}/${pathSegments
        .map(decodeURIComponent)
        .map(encodeURIComponent)
        .join('/')}`,
    );
  } catch (err) {
    const status = (err as { status?: number } | undefined)?.status;
    if (status === 404) return;
    console.warn('[photoUpload] Failed to delete blob:', url, err);
  }
}
